import { CarDataType } from 'data/types';
import React, { FC, useEffect, useState } from "react";
import NcImage from 'shared/NcImage/NcImage';
import { getAvatar, contactApi } from "services/apiServices";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faCog, faCheck } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

export interface FleetsreqCardProps {
    data?: CarDataType;
}

const FleetsreqCard: FC<FleetsreqCardProps> = ({ data }) => {
    const fleetMediaData = data as (CarDataType & {
        exterior_images?: string;
        image?: string;
        brand?: string;
        transmission?: string;
        year?: string | number;
        category?: string;
    }) | undefined;

    const [profileImg, setProfileImg] = useState<any>("");
    
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [message, setMessage] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const getProfile = async () => {
            let filename = '';
            
            if (data?.featuredImage) {
                filename = data.featuredImage;
            } else if (fleetMediaData?.exterior_images) {
                try {
                    const images = JSON.parse(fleetMediaData.exterior_images);
                    if (Array.isArray(images) && images.length > 0) {
                        filename = images[0];
                    }
                } catch (e) {
                    console.error("Failed to parse exterior_images:", e);
                    filename = '';
                }
            }

            if (filename) {
                try {
                    const profile: any = await getAvatar(filename);
                    setProfileImg(URL.createObjectURL(profile))
                } catch (error) {
                    setProfileImg("")
                }
            } else {
                setProfileImg("")
            }
        };

        if (fleetMediaData?.image || (fleetMediaData?.exterior_images && fleetMediaData.exterior_images !== '[]')) {
            getProfile();
        } else if (data) {
            getProfile();
        }
        
    }, [data]);

    const handleRequestNow = async () => {
        if (!name || !email || !data) {
            toast.error("Please fill out your name and email.");
            return;
        }

        setIsSubmitting(true);
        
        const compositeMessage = 
`Fleet Request Details:

Requester Name: ${name}
Requester Email: ${email}
Requester Phone: ${phoneNumber || 'N/A'}
Requested Fleet: ${fleetMediaData?.brand || 'N/A'} - ${data?.model || 'N/A'}
Fleet ID: ${data?.id || 'N/A'}

--- User's Custom Message ---
${message}
-----------------------------`;

        const requestData = {
            templateName: "NEW-EMAIL",
            sender: email,
            name: name,
            message: compositeMessage,
            subject: "Fleet Request"
        };
        
        try {
            const response: any = await contactApi(requestData);
            
            if (response.data.response === "success") {
                toast.success("Your fleet request has been sent successfully!"); 
                setName("");
                setEmail("");
                setMessage("");
                setPhoneNumber("");
            } else {
                toast.error(response.data.message || "Failed to send request. Please try again.");
            }
        } catch (error: any) {
            console.error("Fleet request failed:", error);
            const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
            toast.error(`Error sending request: ${errorMessage}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 px-6 py-4">
                <span className="inline-flex items-center gap-2 bg-amber-500 bg-opacity-20 text-amber-600 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                    Requested Fleet
                </span>
                <h2 className="text-2xl font-bold text-white">{data?.title || 'Fleet Request'}</h2>
            </div>

            {/* Content Grid - Responsive layout */}
            <div className="p-6 space-y-8">
                {/* Fleet Details Section */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Image Section */}
                    <div className="md:col-span-1">
                        <div className="relative w-full h-56 md:h-64 rounded-lg overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                            <NcImage
                                src={profileImg}
                                alt={data?.title || "Car Image"}
                                className="object-cover w-full h-full hover:scale-105 transition-transform duration-300"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                        </div>
                    </div>

                    {/* Details Section */}
                    <div className="md:col-span-2 flex flex-col justify-between">
                        {/* Main Details */}
                        <div className="space-y-4">
                            {/* Model & Badge */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div>
                                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">{data?.model}</p>
                                    <h3 className="text-lg font-bold text-gray-900 mt-1">{data?.title}</h3>
                                </div>
                                <span className="inline-flex items-center bg-green-50 text-green-700 px-3 py-1 rounded-lg text-xs font-semibold whitespace-nowrap">
                                    <FontAwesomeIcon icon={faCheck} className="mr-1 text-green-600" />
                                    Available
                                </span>
                            </div>

                            {/* Specs Grid */}
                            <div className="grid grid-cols-2 gap-3 py-4 border-y border-gray-100">
                                {/* Passengers */}
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                        <FontAwesomeIcon icon={faUsers} className="text-blue-600 text-sm" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Capacity</p>
                                        <p className="text-sm font-semibold text-gray-900">{data?.seats || 0} Passengers</p>
                                    </div>
                                </div>

                                {/* Transmission */}
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                                        <FontAwesomeIcon icon={faCog} className="text-amber-600 text-sm" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500">Transmission</p>
                                        <p className="text-sm font-semibold text-gray-900">{fleetMediaData?.transmission || 'Auto'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Additional Info */}
                            <div className="pt-2 space-y-2">
                                <p className="text-xs text-gray-500">Year: <span className="font-semibold text-gray-700">{fleetMediaData?.year || 'N/A'}</span></p>
                                <p className="text-xs text-gray-500">Type: <span className="font-semibold text-gray-700">{fleetMediaData?.category || 'Premium'}</span></p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Request Form Section */}
                <div className="pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-6">Send Your Request</h3>
                    
                    <div className="space-y-5">
                        {/* Name Input */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Your Name *</label>
                            <input
                                type="text"
                                placeholder="John Doe"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition"
                            />
                        </div>

                        {/* Email Input */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Your Email *</label>
                            <input
                                type="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition"
                            />
                        </div>

                        {/* Phone Input */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                            <input
                                type="tel"
                                placeholder="+1 (555) 000-0000"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition"
                            />
                        </div>

                        {/* Message Textarea */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                            <textarea
                                placeholder="Tell us about your request or any special requirements..."
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={4}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition resize-none"
                            />
                        </div>

                        {/* Submit Button */}
                        <button
                            onClick={handleRequestNow}
                            disabled={isSubmitting}
                            className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-amber-400 text-white font-semibold py-3 rounded-lg transition-colors duration-200"
                        >
                            {isSubmitting ? "Sending..." : "Send Request"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FleetsreqCard;
